import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  Typography
} from '@mui/material';
import type { Usuario, CreateUsuarioRequest } from '../types/usuario.types';
import { initialUsuarioState, initialCreateUsuarioRequest } from '../types/usuario.types';
import { usuarioService } from '../services/usuarioService';
import { useUsuarioStore } from '../store/usuarioStore';

interface UsuarioDialogProps {
  open: boolean;
  onClose: () => void;
  usuario?: Usuario;
  onSuccess: () => void;
}

export const UsuarioDialog = ({ open, onClose, usuario, onSuccess }: UsuarioDialogProps) => {
  const [formData, setFormData] = useState<Usuario>(initialUsuarioState);
  const [createRequest, setCreateRequest] = useState<CreateUsuarioRequest>(initialCreateUsuarioRequest);
  const { addUsuario, updateUsuario } = useUsuarioStore();
  const isEditing = usuario && usuario.id !== 0;

  useEffect(() => {
    if (usuario) {
      setFormData(usuario);
    } else {
      setFormData(initialUsuarioState);
      setCreateRequest(initialCreateUsuarioRequest);
    }
  }, [usuario, open]);

  const handleChange = (field: keyof Usuario, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateRequestChange = (field: keyof CreateUsuarioRequest, value: any) => {
    setCreateRequest(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        // Actualizar usuario existente
        const saved = await usuarioService.save(formData);
        updateUsuario(saved);
      } else {
        // Crear nuevo usuario con persona
        const saved = await usuarioService.createConPersona(createRequest);
        addUsuario(saved);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
      </DialogTitle>
      <DialogContent>
        {isEditing ? (
          // Formulario de edicion (solo campos de usuario)
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nombre de Usuario"
                value={formData.userName}
                onChange={(e) => handleChange('userName', e.target.value)}
                required
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isLocked}
                    onChange={(e) => handleChange('isLocked', e.target.checked)}
                  />
                }
                label="Bloqueado"
              />
            </Grid>
          </Grid>
        ) : (
          // Formulario de creacion (usuario + persona)
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Datos de la Persona
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={createRequest.personaContacto}
                onChange={(e) => handleCreateRequestChange('personaContacto', e.target.value)}
                required
                helperText="Nombre completo de la persona"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Identificacion (DNI/RUC)"
                value={createRequest.identificacion}
                onChange={(e) => handleCreateRequestChange('identificacion', e.target.value)}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefono"
                value={createRequest.telefonoContacto || ''}
                onChange={(e) => handleCreateRequestChange('telefonoContacto', e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Correo Electronico"
                type="email"
                value={createRequest.correoContacto || ''}
                onChange={(e) => handleCreateRequestChange('correoContacto', e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Datos del Usuario
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre de Usuario"
                value={createRequest.userName}
                onChange={(e) => handleCreateRequestChange('userName', e.target.value)}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contrasena"
                type="password"
                value={createRequest.password}
                onChange={(e) => handleCreateRequestChange('password', e.target.value)}
                required
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createRequest.isActive}
                    onChange={(e) => handleCreateRequestChange('isActive', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createRequest.isLocked}
                    onChange={(e) => handleCreateRequestChange('isLocked', e.target.checked)}
                  />
                }
                label="Bloqueado"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEditing ? 'Guardar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsuarioDialog;
